import React, { useState } from 'react';
import { LinkPromptDialog } from '../../common/LinkPromptDialog';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatListBulleted,
    FormatListNumbered,
    FormatQuote,
    Code,
    Link as LinkIcon,
    FormatClear
} from '@mui/icons-material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
// Issue #141: TextStyle + FontSize aus dem offiziellen @tiptap/extension-text-style.
// Erlaubt freie Schriftgroessen zusaetzlich zu Headings.
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
// Issue #141 follow-up: Aufzaehlungs-Marker reagieren auf Bold/Italic/Schriftgroesse.
import { ListItemWithStyle } from './extensions/ListItemWithStyle';

interface InfoTextEditorProps {
    content: any;
    onChange: (content: any) => void;
}

const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '32px', '48px'];

const InfoTextEditor: React.FC<InfoTextEditorProps> = ({ content, onChange }) => {
    const editor = useEditor({
        extensions: [
            // listItem aus dem StarterKit deaktivieren — wir liefern die
            // erweiterte ListItemWithStyle-Variante (Issue #141 follow-up).
            StarterKit.configure({ listItem: false }),
            ListItemWithStyle,
            TiptapLink.configure({
                openOnClick: false,
            }),
            Underline,
            // Issue #141: TextStyle + Custom FontSize-Mark erlauben dem User,
            // freie Schriftgroessen zu setzen — zusaetzlich zu Headings.
            TextStyle,
            FontSize,
        ],
        content: content?.html || '<p>Beginnen Sie hier mit dem Schreiben...</p>',
        onUpdate: ({ editor }) => {
            onChange({
                html: editor.getHTML(),
                text: editor.getText()
            });
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm sm:prose lg:prose-lg mx-auto focus:outline-none',
                style: 'min-height: 300px; padding: 16px;'
            },
        },
    });

    if (!editor) {
        return null;
    }

    const [linkDialogOpen, setLinkDialogOpen] = useState(false);

    const openLinkDialog = () => setLinkDialogOpen(true);

    const applyLink = (url: string | null) => {
        if (url === null) return; // Abbrechen
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Informationstext
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Dieser Lektionstyp zeigt einen formatierten Text an, um den Teilnehmenden zusätzliche
                Informationen zwischen den eigentlichen Aufgaben zu vermitteln.
            </Typography>

            <Paper variant="outlined" sx={{ mb: 2 }}>
                <Box sx={{
                    p: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                }}>
                    {/* Text style / heading dropdown */}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                            value={
                                editor.isActive('heading', { level: 1 }) ? 'h1'
                                : editor.isActive('heading', { level: 2 }) ? 'h2'
                                : editor.isActive('heading', { level: 3 }) ? 'h3'
                                : editor.isActive('heading', { level: 4 }) ? 'h4'
                                : 'paragraph'
                            }
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'paragraph') {
                                    editor.chain().focus().setParagraph().run();
                                } else {
                                    const level = parseInt(val.replace('h', '')) as 1 | 2 | 3 | 4;
                                    editor.chain().focus().toggleHeading({ level }).run();
                                }
                            }}
                            size="small"
                            sx={{ fontSize: '0.8rem', height: 32 }}
                        >
                            <MenuItem value="paragraph" sx={{ fontSize: '0.875rem' }}>Normal</MenuItem>
                            <MenuItem value="h1" sx={{ fontSize: '1.5rem', fontWeight: 700 }}>Überschrift 1</MenuItem>
                            <MenuItem value="h2" sx={{ fontSize: '1.25rem', fontWeight: 700 }}>Überschrift 2</MenuItem>
                            <MenuItem value="h3" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Überschrift 3</MenuItem>
                            <MenuItem value="h4" sx={{ fontSize: '1rem', fontWeight: 600 }}>Überschrift 4</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Issue #141: Schriftgroesse-Picker — fuer freie Groesse jenseits der Headings */}
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <Select
                            value={editor.getAttributes('textStyle')?.fontSize || ''}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (!v) {
                                    (editor.chain().focus() as any).unsetFontSize().run();
                                } else {
                                    (editor.chain().focus() as any).setFontSize(v).run();
                                }
                            }}
                            displayEmpty
                            sx={{ fontSize: '0.8rem', height: 32 }}
                        >
                            <MenuItem value="">
                                <em>Auto</em>
                            </MenuItem>
                            {FONT_SIZES.map((s) => (
                                <MenuItem key={s} value={s} sx={{ fontSize: '0.875rem' }}>
                                    {s.replace('px', ' px')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Divider orientation="vertical" flexItem />

                    <ToggleButtonGroup size="small" aria-label="text formatting">
                        <ToggleButton
                            value="bold"
                            selected={editor.isActive('bold')}
                            onClick={() => editor.chain().focus().toggleBold().run()}
                        >
                            <FormatBold fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                            value="italic"
                            selected={editor.isActive('italic')}
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                        >
                            <FormatItalic fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                            value="underline"
                            selected={editor.isActive('underline')}
                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                        >
                            <FormatUnderlined fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Divider orientation="vertical" flexItem />

                    <ToggleButtonGroup size="small" aria-label="lists">
                        <ToggleButton
                            value="bulletList"
                            selected={editor.isActive('bulletList')}
                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                        >
                            <FormatListBulleted fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                            value="orderedList"
                            selected={editor.isActive('orderedList')}
                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        >
                            <FormatListNumbered fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Divider orientation="vertical" flexItem />

                    <ToggleButtonGroup size="small" aria-label="blocks">
                        <ToggleButton
                            value="blockquote"
                            selected={editor.isActive('blockquote')}
                            onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        >
                            <FormatQuote fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                            value="codeBlock"
                            selected={editor.isActive('codeBlock')}
                            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        >
                            <Code fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>

                    <Divider orientation="vertical" flexItem />

                    <IconButton
                        size="small"
                        onClick={openLinkDialog}
                        color={editor.isActive('link') ? 'primary' : 'default'}
                    >
                        <LinkIcon fontSize="small" />
                    </IconButton>
                    <LinkPromptDialog
                        open={linkDialogOpen}
                        initialUrl={editor.getAttributes('link').href || ''}
                        onClose={() => setLinkDialogOpen(false)}
                        onSubmit={applyLink}
                    />

                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                    >
                        <FormatClear fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{
                    minHeight: 300,
                    '& .ProseMirror': {
                        outline: 'none',
                        padding: 2,
                        '& p': { marginBottom: 1 },
                        '& h1': { fontSize: '2em', fontWeight: 700, marginTop: 2, marginBottom: 1 },
                        '& h2': { fontSize: '1.5em', fontWeight: 700, marginTop: 2, marginBottom: 1 },
                        '& h3': { fontSize: '1.17em', fontWeight: 600, marginTop: 1.5, marginBottom: 1 },
                        '& h4': { fontSize: '1em', fontWeight: 600, marginTop: 1.5, marginBottom: 1 },
                        '& ul, & ol': { paddingLeft: 3, marginBottom: 1 },
                        // Issue #141 follow-up: Marker erbt Bold/Italic/FontSize
                        // vom umgebenden <li> (das Attribute setzt die ListItem-
                        // WithStyle-Extension).
                        '& li::marker': {
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            fontStyle: 'inherit',
                        },
                        '& blockquote': {
                            borderLeft: '4px solid #e0e0e0',
                            paddingLeft: 2,
                            fontStyle: 'italic',
                            marginLeft: 0,
                            color: 'text.secondary'
                        },
                        '& pre': {
                            backgroundColor: '#f5f5f5',
                            padding: 2,
                            borderRadius: 1,
                            overflowX: 'auto'
                        },
                        '& code': {
                            backgroundColor: '#f5f5f5',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontFamily: 'monospace'
                        },
                        '& a': {
                            color: 'primary.main',
                            textDecoration: 'underline'
                        }
                    }
                }}>
                    <EditorContent editor={editor} />
                </Box>
            </Paper>
        </Box>
    );
};

export default InfoTextEditor;
